<template>
    <main class="job-insights">
        <section class="job-insights__pipeline" aria-labelledby="job-pipeline-title">
            <header class="job-insights__header">
                <div>
                    <p class="job-insights__eyebrow">Pipeline</p>
                    <h1 id="job-pipeline-title" class="job-insights__title">Kandidaten per vacature en status</h1>
                    <p class="job-insights__intro">
                        Het actuele aantal sollicitaties in elke fase van het selectieproces.
                    </p>
                </div>

                <div class="job-insights__filters">
                    <label v-if="canSelectScope" class="job-insights__filter">
                        <span>Dashboard</span>
                        <div class="job-insights__filter-select">
                            <select v-model="insightsScope" @change="changeInsightsScope">
                                <option value="all">Alle vacatures</option>
                                <option value="personal">Mijn vacatures</option>
                            </select>
                            <i class="ak-icon" aria-hidden="true">keyboard_arrow_down</i>
                        </div>
                    </label>

                    <label class="job-insights__filter">
                        <span>Vacature filters</span>
                        <div class="job-insights__filter-select">
                            <select v-model="jobFilter">
                                <option value="all">Alle vacatures</option>
                                <option value="published">Enkel gepubliceerde vacatures</option>
                                <option value="has-reactions">Met minstens één sollicitatie</option>
                            </select>
                            <i class="ak-icon" aria-hidden="true">keyboard_arrow_down</i>
                        </div>
                    </label>
                </div>
            </header>

            <p v-if="loading" class="job-insights__state">Pipeline laden…</p>
            <p v-else-if="errorMessage" class="job-insights__state job-insights__state--error">{{ errorMessage }}</p>

            <template v-else-if="hasPipelineData && filteredJobs.length">
                <div class="job-insights__table-wrapper">
                    <table class="job-insights__table">
                        <thead>
                            <tr>
                                <th scope="col">Vacature</th>
                                <th v-for="status in statuses" :key="status.id" scope="col">
                                    {{ status.title }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="job in filteredJobs" :key="job.id">
                                <th scope="row">
                                    <button
                                        v-if="canEditJobs"
                                        type="button"
                                        class="job-insights__job-link"
                                        :aria-label="`${job.title} bewerken`"
                                        @click="editJob(job.objectId)"
                                    >
                                        {{ job.title }}
                                    </button>
                                    <span v-else class="job-insights__job-title">{{ job.title }}</span>
                                    <a
                                        v-if="job.websiteUrl"
                                        class="job-insights__website-link"
                                        :href="job.websiteUrl"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        Bekijk op website
                                        <i class="ak-icon" aria-hidden="true">open_in_new</i>
                                    </a>
                                </th>
                                <td v-for="status in statuses" :key="status.id">
                                    <router-link
                                        class="job-insights__count-link"
                                        :to="getReactionGridRoute(job.id, status.id)"
                                        :aria-label="`${job.counts[status.id] || 0} sollicitaties voor ${job.title} met status ${status.title} bekijken`"
                                    >
                                        {{ job.counts[status.id] || 0 }}
                                    </router-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    class="job-insights__summary-grid"
                    :class="{ 'job-insights__summary-grid--lead-times-expanded': showAllLeadTimes }"
                >
                    <section class="job-insights__status-overview" aria-labelledby="status-overview-title">
                        <div class="job-insights__section-heading">
                            <p class="job-insights__eyebrow">Status verdeling</p>
                            <h2 id="status-overview-title">Verdeling per status</h2>
                        </div>

                        <ul class="job-insights__status-list">
                            <li v-for="status in statusSummaries" :key="status.id" class="job-insights__status-item">
                                <span class="job-insights__status-name">{{ status.title }}</span>
                                <span
                                    class="job-insights__status-track"
                                    role="progressbar"
                                    :aria-label="`${status.title}: ${status.count} van ${status.totalCount} sollicitaties`"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    :aria-valuenow="status.percentage"
                                >
                                    <span
                                        class="job-insights__status-bar job-insights__status-bar--yellow"
                                        :style="{ width: `${status.percentage}%` }"
                                    ></span>
                                </span>
                                <strong class="job-insights__status-count">
                                    <span>{{ status.count }}</span>
                                    <small>van {{ status.totalCount }}</small>
                                </strong>
                            </li>
                        </ul>
                    </section>

                    <section class="job-insights__lead-time-overview" aria-labelledby="lead-time-overview-title">
                        <div class="job-insights__section-heading">
                            <p class="job-insights__eyebrow">Doorlooptijd</p>
                            <h2 id="lead-time-overview-title">Per vacature</h2>
                        </div>

                        <p v-if="loadingLeadTimes" class="job-insights__overview-state">Doorlooptijden laden…</p>
                        <p v-else-if="leadTimesErrorMessage" class="job-insights__overview-state job-insights__overview-state--error">
                            {{ leadTimesErrorMessage }}
                        </p>
                        <ul v-else-if="leadTimes.length" class="job-insights__lead-time-list">
                            <li v-for="leadTime in visibleLeadTimes" :key="leadTime.id" class="job-insights__lead-time-item">
                                <span class="job-insights__lead-time-title">{{ leadTime.title }}</span>
                                <strong class="job-insights__lead-time-value">
                                    {{ formatDays(leadTime.averageDays) }}
                                </strong>
                                <span class="job-insights__lead-time-caption">
                                    gemiddeld over {{ leadTime.reactionCount }} afgeronde sollicitatie{{ leadTime.reactionCount === 1 ? '' : 's' }}
                                </span>
                            </li>
                        </ul>
                        <button
                            v-if="hasMoreLeadTimes"
                            type="button"
                            class="job-insights__lead-time-toggle"
                            @click="showAllLeadTimes = !showAllLeadTimes"
                        >
                            {{ showAllLeadTimes ? 'Minder tonen' : 'Meer tonen' }}
                        </button>
                        <p
                            v-if="!loadingLeadTimes && !leadTimesErrorMessage && !leadTimes.length"
                            class="job-insights__overview-state"
                        >
                            Geen afgeronde sollicitaties voor deze filter.
                        </p>
                    </section>
                </div>
            </template>

            <p v-else-if="hasPipelineData" class="job-insights__state">
                Geen vacatures gevonden voor deze filter.
            </p>

            <p v-else class="job-insights__state">
                Er zijn nog geen vacatures of sollicitatiestatussen beschikbaar.
            </p>

            <section class="job-insights__history-overview" aria-labelledby="history-overview-title">
                <div class="job-insights__section-heading">
                    <p class="job-insights__eyebrow">Activiteit</p>
                    <h2 id="history-overview-title">Historiek van acties</h2>
                </div>

                <p v-if="loadingHistory" class="job-insights__overview-state">Historiek laden…</p>
                <template v-else>
                    <p
                        v-if="historyErrorMessage"
                        class="job-insights__overview-state job-insights__overview-state--error"
                    >
                        {{ historyErrorMessage }}
                    </p>
                    <ol v-if="history.length" class="job-insights__history-list">
                        <li v-for="entry in history" :key="entry.id" class="job-insights__history-item">
                            <span class="job-insights__history-marker" aria-hidden="true">
                                <i class="ak-icon">{{ getHistoryActorIcon(entry.actor.type) }}</i>
                            </span>
                            <div class="job-insights__history-content">
                                <div class="job-insights__history-heading">
                                    <strong>{{ entry.description }}</strong>
                                    <time>{{ entry.createdAt || '–' }}</time>
                                </div>
                                <router-link
                                    class="job-insights__history-reaction"
                                    :to="{ name: 'reaction-overview', params: { hashId: entry.reactionHashId } }"
                                >
                                    {{ entry.candidateName }} · {{ entry.jobTitle }}
                                </router-link>
                                <span class="job-insights__history-actor">
                                    {{ getHistoryActorLabel(entry.actor.type) }}: {{ entry.actor.name }}
                                </span>
                            </div>
                        </li>
                    </ol>
                    <p v-else-if="!historyErrorMessage" class="job-insights__overview-state">
                        Nog geen acties beschikbaar voor dit overzicht.
                    </p>
                </template>
                <button
                    v-if="!loadingHistory && hasMoreHistory"
                    type="button"
                    class="job-insights__history-toggle"
                    :disabled="loadingMoreHistory"
                    @click="getHistory(true)"
                >
                    {{ loadingMoreHistory ? 'Meer laden…' : 'Meer tonen' }}
                </button>
            </section>
        </section>
        <router-view name="childview" />
    </main>
</template>

<script>
    export default {
        name: 'JobInsights',
        data() {
            return {
                jobs: [],
                statuses: [],
                leadTimes: [],
                history: [],
                jobFilter: 'published',
                insightsScope: 'personal',
                canSelectScope: false,
                canEditJobs: true,
                loading: true,
                errorMessage: null,
                loadingLeadTimes: true,
                leadTimesErrorMessage: null,
                loadingHistory: true,
                loadingMoreHistory: false,
                historyErrorMessage: null,
                historyCursor: null,
                hasMoreHistory: false,
                historyRequestId: 0,
                showAllLeadTimes: false,
            };
        },
        computed: {
            hasPipelineData() {
                return this.jobs.length && this.statuses.length;
            },
            filteredJobs() {
                if (this.jobFilter === 'published') {
                    return this.jobs.filter(job => job.isPublished);
                }

                if (this.jobFilter === 'has-reactions') {
                    return this.jobs.filter(job => this.getReactionCount(job) > 0);
                }

                return this.jobs;
            },
            statusSummaries() {
                const totalReactionCount = this.filteredJobs.reduce(
                    (total, job) => total + this.getReactionCount(job),
                    0,
                );

                return this.statuses.map(status => {
                    const count = this.filteredJobs.reduce(
                        (total, job) => total + (job.counts[status.id] || 0),
                        0,
                    );

                    return {
                        ...status,
                        count,
                        totalCount: totalReactionCount,
                        percentage: totalReactionCount ? (count / totalReactionCount) * 100 : 0,
                    };
                });
            },
            visibleLeadTimes() {
                return this.showAllLeadTimes ? this.leadTimes : this.leadTimes.slice(0, 4);
            },
            hasMoreLeadTimes() {
                return this.leadTimes.length > 4;
            },
        },
        created() {
            this.getPipeline();
            this.getLeadTimes();
            this.getHistory();
        },
        methods: {
            editJob(objectId) {
                this.$router.push({
                    name: 'job-insights-form',
                    params: { bundle: 'job', definition: 'form', objectId },
                });
            },
            getReactionGridRoute(jobId, statusId) {
                return {
                    name: 'grid',
                    params: {
                        gridDefinition: this.insightsScope === 'personal' ? 'inbox-grid' : 'grid',
                        bundle: 'reaction',
                    },
                    query: {
                        f0: 'jobId',
                        'v0[0]': 'EQ',
                        'v0[1]': jobId,
                        f1: 'statusId',
                        'v1[0]': 'EQ',
                        'v1[1]': statusId,
                    },
                };
            },
            getReactionCount(job) {
                return this.statuses.reduce((count, status) => count + (job.counts[status.id] || 0), 0);
            },
            formatDays(days) {
                return `${String(days).replace('.', ',')} dagen`;
            },
            getHistoryActorIcon(actorType) {
                return {
                    candidate: 'mail',
                    system: 'settings',
                    user: 'person',
                }[actorType] || 'history';
            },
            getHistoryActorLabel(actorType) {
                return {
                    candidate: 'Kandidaat',
                    system: 'Bron',
                    user: 'Gebruiker',
                }[actorType] || 'Actor';
            },
            async changeInsightsScope() {
                await Promise.all([
                    this.getPipeline(),
                    this.getLeadTimes(),
                    this.getHistory(),
                ]);
            },
            async getHistory(append = false) {
                const requestId = ++this.historyRequestId;

                if (append) {
                    this.loadingMoreHistory = true;
                } else {
                    this.loadingHistory = true;
                    this.loadingMoreHistory = false;
                    this.history = [];
                    this.historyCursor = null;
                    this.hasMoreHistory = false;
                }
                this.historyErrorMessage = null;

                try {
                    const result = await this.$get('/job-insights/history', {
                        scope: this.insightsScope,
                        beforeId: append ? this.historyCursor : null,
                    });

                    if (requestId !== this.historyRequestId) {
                        return;
                    }

                    if (!result.success) {
                        this.historyErrorMessage = result.errorMessage || 'De historiek kon niet worden geladen.';
                        return;
                    }

                    const historyPage = result.history || {};
                    const historyItems = historyPage.items || [];
                    this.history = append ? this.history.concat(historyItems) : historyItems;
                    this.historyCursor = historyPage.nextCursor || null;
                    this.hasMoreHistory = Boolean(historyPage.hasMore);
                } catch (error) {
                    if (requestId === this.historyRequestId) {
                        this.historyErrorMessage = 'De historiek kon niet worden geladen.';
                    }
                } finally {
                    if (requestId === this.historyRequestId) {
                        if (append) {
                            this.loadingMoreHistory = false;
                        } else {
                            this.loadingHistory = false;
                        }
                    }
                }
            },
            async getLeadTimes() {
                this.loadingLeadTimes = true;
                this.leadTimesErrorMessage = null;

                try {
                    const result = await this.$get('/job-insights/lead-times', {
                        scope: this.insightsScope,
                    });

                    if (!result.success) {
                        this.leadTimesErrorMessage = result.errorMessage || 'De doorlooptijden konden niet worden geladen.';
                        return;
                    }

                    this.leadTimes = result.leadTimes || [];
                    this.showAllLeadTimes = false;
                } catch (error) {
                    this.leadTimesErrorMessage = 'De doorlooptijden konden niet worden geladen.';
                } finally {
                    this.loadingLeadTimes = false;
                }
            },
            async getPipeline() {
                this.loading = true;
                this.errorMessage = null;

                try {
                    const result = await this.$get('/job-insights/pipeline', {
                        scope: this.insightsScope,
                    });

                    if (!result.success) {
                        this.errorMessage = result.errorMessage || 'De pipeline kon niet worden geladen.';
                        return;
                    }

                    this.jobs = result.pipeline.jobs || [];
                    this.statuses = result.pipeline.statuses || [];
                    const insights = result.insights || {};
                    this.insightsScope = insights.scope || 'all';
                    this.canSelectScope = Boolean(insights.canSelectScope);
                    this.canEditJobs = insights.canEditJobs !== false;
                } catch (error) {
                    this.errorMessage = 'De pipeline kon niet worden geladen.';
                } finally {
                    this.loading = false;
                }
            },
        },
    }
</script>
